import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRightLeft, ArrowLeft, Plus, Trash2, Search, Warehouse, Store, Package, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { stockTransfersAPI, warehousesAPI, storesAPI, inventoryAPI } from '@/services/api';

const TransferCreatePage = () => {
  const [fromLocationType, setFromLocationType] = useState('warehouse');
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationType, setToLocationType] = useState('store');
  const [toLocationId, setToLocationId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  
  const [warehouses, setWarehouses] = useState([]);
  const [stores, setStores] = useState([]);
  const [availableStock, setAvailableStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Product search
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch warehouses and stores on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const [whRes, stRes] = await Promise.all([
          warehousesAPI.list({ is_active: true }),
          storesAPI.list({ is_active: true })
        ]);
        setWarehouses(whRes.data || []);
        setStores(stRes.data || []);
        
        // Auto-select first warehouse
        if (whRes.data?.length > 0) {
          setFromLocationId(whRes.data[0].id);
        }
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to fetch locations', variant: 'destructive' });
      }
    };
    fetchLocations();
  }, [toast]);

  // Fetch available stock when source changes
  const fetchAvailableStock = useCallback(async () => {
    if (!fromLocationId) {
      setAvailableStock([]);
      return;
    }
    
    setStockLoading(true);
    try {
      if (fromLocationType === 'warehouse') {
        const response = await inventoryAPI.getWarehouseStock(fromLocationId, { view: 'active' });
        setAvailableStock(response.data?.items || []);
      } else {
        // For stores, we'd need a similar endpoint
        setAvailableStock([]);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch available stock', variant: 'destructive' });
    } finally {
      setStockLoading(false);
    }
  }, [fromLocationId, fromLocationType, toast]);

  useEffect(() => {
    fetchAvailableStock();
    // Clear items when source changes
    setItems([]);
  }, [fetchAvailableStock]);

  const handleAddProduct = (stockItem) => {
    // Check if already added
    const existing = items.find(i => i.product_id === stockItem.product_id && i.batch_number === stockItem.batch_number);
    if (existing) {
      toast({ title: 'Info', description: 'Product already added to transfer', variant: 'default' });
      setProductSearchOpen(false);
      return;
    }
    
    setItems([...items, {
      product_id: stockItem.product_id,
      product_name: stockItem.product_name,
      product_sku: stockItem.product_sku,
      uom: stockItem.uom || 'PCS',
      available_qty: stockItem.quantity,
      quantity: 1,
      batch_number: stockItem.batch_number,
      cost_price: stockItem.cost_price || 0
    }]);
    
    setProductSearchOpen(false);
    setProductSearch('');
  };

  const handleUpdateQuantity = (index, qty) => {
    const item = items[index];
    if (qty > item.available_qty) {
      toast({ title: 'Warning', description: `Max available: ${item.available_qty}`, variant: 'destructive' });
      return;
    }
    const updated = [...items];
    updated[index].quantity = Math.max(0, qty);
    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = async (isDraft = false) => {
    // Validation
    if (!fromLocationId) {
      toast({ title: 'Error', description: 'Select source location', variant: 'destructive' });
      return;
    }
    if (!toLocationId) {
      toast({ title: 'Error', description: 'Select destination location', variant: 'destructive' });
      return;
    }
    if (fromLocationId === toLocationId && fromLocationType === toLocationType) {
      toast({ title: 'Error', description: 'Source and destination cannot be the same', variant: 'destructive' });
      return;
    }
    if (items.length === 0) {
      toast({ title: 'Error', description: 'Add at least one item', variant: 'destructive' });
      return;
    }
    
    const invalidItems = items.filter(i => i.quantity <= 0 || i.quantity > i.available_qty);
    if (invalidItems.length > 0) {
      toast({ title: 'Error', description: 'Check item quantities', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        from_location_type: fromLocationType,
        from_location_id: fromLocationId,
        to_location_type: toLocationType,
        to_location_id: toLocationId,
        transfer_date: transferDate,
        items: items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          batch_number: i.batch_number
        })),
        notes: notes || null,
        save_as_draft: isDraft  // Draft: save locally, Create: visible to source/destination
      };
      
      await stockTransfersAPI.create(payload);
      
      if (isDraft) {
        toast({ title: 'Success', description: 'Transfer saved as draft (not visible to source/destination)' });
      } else {
        toast({ title: 'Success', description: 'Transfer created and sent to source for processing' });
      }
      
      navigate('/hq/inventory/transfers');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create transfer', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const getFromLocations = () => fromLocationType === 'warehouse' ? warehouses : stores;
  const getToLocations = () => toLocationType === 'warehouse' ? warehouses : stores;

  const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalValue = items.reduce((sum, i) => sum + (i.quantity * i.cost_price), 0);

  // Filter stock for search
  const filteredStock = availableStock.filter(s => {
    if (!productSearch) return true;
    const search = productSearch.toLowerCase();
    return s.product_name?.toLowerCase().includes(search) || 
           s.product_sku?.toLowerCase().includes(search);
  });

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="transfer-create-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/hq/inventory/transfers')}
          className="text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">New Stock Transfer</h1>
          <p className="text-gray-500">Transfer stock between locations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Transfer Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Source & Destination */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-teal-500" />
                Transfer Route
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* From */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-700 font-semibold">
                    <Package className="w-4 h-4" /> From (Source)
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Location Type</Label>
                    <Select value={fromLocationType} onValueChange={setFromLocationType}>
                      <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="from-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="warehouse">
                          <div className="flex items-center gap-2">
                            <Warehouse className="w-4 h-4" /> Warehouse
                          </div>
                        </SelectItem>
                        <SelectItem value="store">
                          <div className="flex items-center gap-2">
                            <Store className="w-4 h-4" /> Store
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Select Location *</Label>
                    <Select value={fromLocationId} onValueChange={setFromLocationId}>
                      <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="from-location-select">
                        <SelectValue placeholder="Select source..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {getFromLocations().map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} ({loc.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* To */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-teal-600 font-semibold">
                    <Package className="w-4 h-4" /> To (Destination)
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Location Type</Label>
                    <Select value={toLocationType} onValueChange={setToLocationType}>
                      <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="to-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="warehouse">
                          <div className="flex items-center gap-2">
                            <Warehouse className="w-4 h-4" /> Warehouse
                          </div>
                        </SelectItem>
                        <SelectItem value="store">
                          <div className="flex items-center gap-2">
                            <Store className="w-4 h-4" /> Store
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Select Location *</Label>
                    <Select value={toLocationId} onValueChange={setToLocationId}>
                      <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="to-location-select">
                        <SelectValue placeholder="Select destination..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {getToLocations().map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} ({loc.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Transfer Date */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-600">Transfer Date</Label>
                  <Input
                    type="date"
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                    className="bg-gray-50 border-gray-300"
                    data-testid="transfer-date-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-gray-800">Transfer Items</CardTitle>
                <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="border-teal-300 text-teal-600 hover:bg-teal-50"
                      disabled={!fromLocationId || stockLoading}
                      data-testid="add-product-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Product
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96 p-0 bg-white border-gray-200" align="end">
                    <Command>
                      <CommandInput 
                        placeholder="Search products..." 
                        value={productSearch}
                        onValueChange={setProductSearch}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {stockLoading ? 'Loading...' : 'No products found'}
                        </CommandEmpty>
                        <CommandGroup heading="Available Stock">
                          {filteredStock.slice(0, 10).map((stock, idx) => (
                            <CommandItem
                              key={`${stock.product_id}-${stock.batch_number || idx}`}
                              onSelect={() => handleAddProduct(stock)}
                              className="cursor-pointer"
                            >
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">{stock.product_name}</p>
                                <p className="text-xs text-teal-600">{stock.product_sku}</p>
                                {stock.batch_number && (
                                  <Badge variant="outline" className="text-xs mt-1">{stock.batch_number}</Badge>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-800">{stock.quantity} {stock.uom}</p>
                                <p className="text-xs text-gray-500">₹{stock.cost_price?.toFixed(2)}</p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No items added</p>
                  <p className="text-sm">Select a source location and add products to transfer</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-gray-700">Product</TableHead>
                        <TableHead className="text-gray-700">Batch</TableHead>
                        <TableHead className="text-gray-700 text-center">Available</TableHead>
                        <TableHead className="text-gray-700 text-center">Transfer Qty</TableHead>
                        <TableHead className="text-gray-700 text-right">Cost</TableHead>
                        <TableHead className="text-gray-700 text-right">Value</TableHead>
                        <TableHead className="text-gray-700 w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={index} className="border-gray-200">
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-teal-600">{item.product_sku}</p>
                          </TableCell>
                          <TableCell className="text-gray-600">{item.batch_number || '-'}</TableCell>
                          <TableCell className="text-center text-gray-500">{item.available_qty} {item.uom}</TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min="1"
                              max={item.available_qty}
                              value={item.quantity}
                              onChange={(e) => handleUpdateQuantity(index, parseFloat(e.target.value) || 0)}
                              className="w-20 text-center bg-gray-50 border-gray-300"
                              data-testid={`qty-input-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-right text-gray-600">₹{item.cost_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium text-gray-800">
                            ₹{(item.quantity * item.cost_price).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveItem(index)}
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`remove-item-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <Label className="text-gray-600">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes for this transfer..."
                className="mt-2 bg-gray-50 border-gray-300"
                rows={3}
                data-testid="notes-input"
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          <Card className="bg-white border-gray-200 shadow-sm sticky top-6">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800">Transfer Summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Items</span>
                <span className="font-medium text-gray-800">{items.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Quantity</span>
                <span className="font-medium text-gray-800">{totalQty}</span>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-semibold">Total Value</span>
                  <span className="text-xl font-bold text-teal-600">₹{totalValue.toLocaleString()}</span>
                </div>
              </div>

              <div className="pt-4 space-y-3">
                <Button 
                  onClick={() => handleSave(true)}
                  disabled={saving || items.length === 0}
                  variant="outline"
                  className="w-full border-gray-300"
                  data-testid="save-draft-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button 
                  onClick={() => handleSave(false)}
                  disabled={saving || items.length === 0}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                  data-testid="create-transfer-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  {saving ? 'Processing...' : 'Create Transfer'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Help */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <p className="font-semibold text-blue-800 mb-2">Transfer Tips</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Select source to see available stock</li>
                <li>• Only items with stock can be added</li>
                <li>• "Create Transfer" sends to source for processing</li>
                <li>• "Save as Draft" keeps it local (not visible to source)</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TransferCreatePage;
